import React, { useState } from 'react';

export interface BulkFunkkanalTrupp {
  id: number;
  name: string;
  aufgabe: string;
  standort: string;
  funkkanal?: string;
  outOfService?: boolean;
}

interface BulkFunkkanalModalProps {
  open: boolean;
  trupps: BulkFunkkanalTrupp[];
  availableChannels: string[];
  saving?: boolean;
  onConfirm: (selectedTruppIds: number[], newChannel: string) => void | Promise<void>;
  onCancel: () => void;
}

const BulkFunkkanalModal: React.FC<BulkFunkkanalModalProps> = ({
  open,
  trupps,
  availableChannels,
  saving = false,
  onConfirm,
  onCancel,
}) => {
  const eligibleTrupps = trupps.filter((t) => !t.outOfService);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(eligibleTrupps.map((t) => t.id))
  );
  const [channel, setChannel] = useState('');
  const [channelSelected, setChannelSelected] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set(eligibleTrupps.map((t) => t.id)));
    setChannel('');
    setChannelSelected(false);
  }, [open, trupps]);

  if (!open) return null;

  const toggleTrupp = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(eligibleTrupps.map((t) => t.id)));
  const selectNone = () => setSelectedIds(new Set());

  const canSubmit =
    !saving &&
    channelSelected &&
    channel.trim().length > 0 &&
    selectedIds.size > 0;

  const formatChannel = (value?: string) => {
    const v = (value || '').trim();
    if (!v) return 'kein Kanal';
    return /^kanal\b/i.test(v) ? v : `Kanal ${v}`;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 6000,
      }}
    >
      <div
        className={window.innerWidth <= 600 ? 'einsatzdetail-modal-mobile' : ''}
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: 28,
          minWidth: 340,
          maxWidth: 520,
          width: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        }}
      >
        <h3 style={{ margin: '0 0 4px 0', color: '#333', fontSize: 20, textAlign: 'center' }}>
          Funkkanal wechseln
        </h3>
        <p
          style={{
            color: '#666',
            fontSize: 14,
            margin: '0 0 16px 0',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          Neuen Kanal wählen und bei Bedarf Trupps abwählen, die nicht umgestellt werden sollen.
        </p>

        {/* Kanalwahl */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: '#333' }}>
            Neuer Funkkanal
          </div>
          {availableChannels.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                justifyContent: 'center',
                marginBottom: 12,
              }}
            >
              {availableChannels.map((fk) => (
                <button
                  key={fk}
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    setChannel(fk);
                    setChannelSelected(true);
                  }}
                  style={{
                    background: channel === fk ? '#c00' : '#f8f9fa',
                    color: channel === fk ? '#fff' : '#333',
                    borderRadius: 8,
                    padding: '10px 14px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: 500,
                    fontSize: 14,
                    border: channel === fk ? '2px solid #c00' : '2px solid #e0e0e0',
                    boxShadow:
                      channel === fk
                        ? '0 4px 12px rgba(204, 0, 0, 0.3)'
                        : '0 2px 4px rgba(0,0,0,0.1)',
                    minWidth: 90,
                  }}
                >
                  {fk}
                </button>
              ))}
            </div>
          )}
          <input
            disabled={saving}
            placeholder={
              availableChannels.length > 0
                ? 'Oder eigenen Funkkanal eingeben...'
                : 'Funkkanal eingeben...'
            }
            value={availableChannels.includes(channel) ? '' : channel}
            onChange={(e) => {
              setChannel(e.target.value);
              setChannelSelected(e.target.value.trim().length > 0);
            }}
            style={{
              fontSize: 16,
              padding: '12px 16px',
              borderRadius: 8,
              border: '2px solid #e0e0e0',
              outline: 'none',
              width: '100%',
              boxSizing: 'border-box',
              textAlign: 'center',
              background: '#fff',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#c00';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#e0e0e0';
            }}
          />
        </div>

        {/* Alle/Keine */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button
            type="button"
            disabled={saving}
            onClick={selectAll}
            style={{
              background: selectedIds.size === eligibleTrupps.length ? '#c00' : '#f5f5f5',
              color: selectedIds.size === eligibleTrupps.length ? '#fff' : '#333',
              border: 'none',
              borderRadius: 6,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Alle auswählen
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={selectNone}
            style={{
              background: selectedIds.size === 0 ? '#c00' : '#f5f5f5',
              color: selectedIds.size === 0 ? '#fff' : '#333',
              border: 'none',
              borderRadius: 6,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Keine auswählen
          </button>
        </div>

        {/* Trupp-Liste */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            marginBottom: 16,
            maxHeight: '36vh',
            border: '1px solid #eee',
            borderRadius: 8,
          }}
        >
          {eligibleTrupps.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>
              Keine aktiven Trupps vorhanden
            </div>
          ) : (
            eligibleTrupps.map((trupp) => {
              const selected = selectedIds.has(trupp.id);
              return (
                <div
                  key={trupp.id}
                  onClick={() => {
                    if (!saving) toggleTrupp(trupp.id);
                  }}
                  style={{
                    padding: '12px 16px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: selected ? '#fff5f5' : 'transparent',
                  }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      flexShrink: 0,
                      border: selected ? '2px solid #c00' : '2px solid #ccc',
                      background: selected ? '#c00' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {selected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#333' }}>{trupp.name}</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      {trupp.aufgabe}
                      {trupp.standort ? ` | ${trupp.standort}` : ''}
                      {' · '}
                      aktuell: {formatChannel(trupp.funkkanal)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div
          style={{
            background: '#f5f5f5',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
            color: '#555',
            textAlign: 'center',
          }}
        >
          {channel.trim() ? (
            <>
              <strong>{selectedIds.size}</strong> von {eligibleTrupps.length} Trupps wechseln auf{' '}
              <strong>{formatChannel(channel.trim())}</strong>.
            </>
          ) : (
            <>Zuerst einen Funkkanal wählen.</>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            style={{
              background: '#eee',
              border: 'none',
              borderRadius: 8,
              padding: '12px 20px',
              fontSize: 15,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              color: '#555',
              flex: 1,
            }}
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onConfirm(Array.from(selectedIds), channel.trim())}
            style={{
              background: canSubmit ? '#c00' : '#ccc',
              border: 'none',
              borderRadius: 8,
              padding: '12px 20px',
              fontSize: 15,
              fontWeight: 700,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              color: '#fff',
              flex: 1,
            }}
          >
            {saving ? 'Wird gespeichert…' : 'Kanal wechseln'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkFunkkanalModal;
